/**
 * Google OAuth 2.0 Service (No Firebase)
 * Handles direct OAuth flow with Google for getting access and refresh tokens
 */

import oauthConfig from '../config/oauth';
import logger from '../utils/logger';

class GoogleOAuthService {
  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/callback`;
    this.scopes = oauthConfig.google.scopes;
    
    // Debug logging
    logger.log('🔐 GoogleOAuthService initialized:', {
      clientId: this.clientId ? '✅ Set' : '❌ Missing',
      redirectUri: this.redirectUri,
      fromEnv: !!process.env.REACT_APP_GOOGLE_REDIRECT_URI,
      autoDetected: !process.env.REACT_APP_GOOGLE_REDIRECT_URI,
      origin: window.location.origin
    });
  }

  /**
   * Generate a random string for PKCE code verifier
   */
  generateCodeVerifier() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  /**
   * Generate code challenge from verifier
   */
  async generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(new Uint8Array(hash));
  }

  /**
   * Base64 URL encode
   */
  base64URLEncode(buffer) {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generate a random state parameter
   */
  generateState() {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  /**
   * Initiate OAuth flow - redirects user to Google consent screen
   * @param {Object} options - Optional parameters
   * @param {string} options.loginHint - Email hint for account selection
   * @param {boolean} options.forceConsent - Force consent screen (default: true for refresh token)
   */
  async initiateOAuthFlow(options = {}) {
    const { loginHint = null, forceConsent = true } = options;

    try {
      // No trial persistence during OAuth initiation — normal flow
      // Generate PKCE parameters
      const codeVerifier = this.generateCodeVerifier();
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);
      const state = this.generateState();

      // Store PKCE parameters in sessionStorage and localStorage as a fallback
      try { sessionStorage.setItem('oauth_code_verifier', codeVerifier); } catch (_) {}
      try { sessionStorage.setItem('oauth_state', state); } catch (_) {}
      try { localStorage.setItem('oauth_code_verifier', codeVerifier); } catch (_) {}
      try { localStorage.setItem('oauth_state', state); } catch (_) {}

      // Build authorization URL
      const params = new URLSearchParams({
        client_id: this.clientId,
        redirect_uri: this.redirectUri,
        response_type: 'code',
        scope: this.scopes.join(' '),
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        access_type: 'offline', // Request refresh token
        prompt: 'consent', // Always force consent to ensure all GMB scopes are granted
        include_granted_scopes: 'true'
      });

      // Add login hint if provided
      if (loginHint) {
        params.append('login_hint', loginHint);
      }

      const authUrl = `${oauthConfig.google.authEndpoint}?${params.toString()}`;
      
      logger.log('Initiating OAuth flow:', { 
        redirectUri: this.redirectUri,
        scopes: this.scopes,
        forceConsent 
      });

      // Redirect to Google
      window.location.href = authUrl;
    } catch (error) {
      logger.error('Error initiating OAuth flow:', error);
      throw new Error('Failed to initiate Google authentication');
    }
  }

  /**
   * Handle OAuth callback - exchange code for tokens
   * @param {string} code - Authorization code from Google
   * @param {string} state - State parameter for validation
   * @returns {Promise<Object>} Token response with access_token and refresh_token
   */
  async handleCallback(code, state) {
    try {
      // Validate state (check sessionStorage first, then localStorage as fallback)
      let storedState = null;
      try { storedState = sessionStorage.getItem('oauth_state'); } catch (_) { storedState = null; }
      if (!storedState) {
        try { storedState = localStorage.getItem('oauth_state'); } catch (_) { storedState = null; }
      }
      if (!storedState || storedState !== state) {
        throw new Error('Invalid state parameter - possible CSRF attack');
      }

      // Get code verifier (sessionStorage preferred, localStorage fallback)
      let codeVerifier = null;
      try { codeVerifier = sessionStorage.getItem('oauth_code_verifier'); } catch (_) { codeVerifier = null; }
      if (!codeVerifier) {
        try { codeVerifier = localStorage.getItem('oauth_code_verifier'); } catch (_) { codeVerifier = null; }
      }
      if (!codeVerifier) {
        throw new Error('Code verifier not found');
      }

      // Exchange code for tokens
      const tokenResponse = await fetch(oauthConfig.google.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
          code: code,
          code_verifier: codeVerifier,
          grant_type: 'authorization_code',
          redirect_uri: this.redirectUri,
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        logger.error('Token exchange failed:', errorData);
        throw new Error(errorData.error_description || 'Failed to exchange code for tokens');
      }

      const tokens = await tokenResponse.json();

      // Clean up stored parameters from both storages
      try { sessionStorage.removeItem('oauth_code_verifier'); } catch (_) {}
      try { sessionStorage.removeItem('oauth_state'); } catch (_) {}
      try { localStorage.removeItem('oauth_code_verifier'); } catch (_) {}
      try { localStorage.removeItem('oauth_state'); } catch (_) {}

      logger.log('Successfully obtained tokens:', {
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        expiresIn: tokens.expires_in
      });

      return tokens;
    } catch (error) {
      logger.error('Error handling OAuth callback:', error);
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token
   * @param {string} refreshToken - The refresh token
   * @returns {Promise<Object>} New token response
   */
  async refreshAccessToken(refreshToken) {
    try {
      const response = await fetch(oauthConfig.google.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: this.clientId,
          client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('Token refresh failed:', errorData);
        throw new Error(errorData.error_description || 'Failed to refresh token');
      }

      const tokens = await response.json();
      
      logger.log('Successfully refreshed token:', {
        hasAccessToken: !!tokens.access_token,
        expiresIn: tokens.expires_in
      });

      return tokens;
    } catch (error) {
      logger.error('Error refreshing access token:', error);
      throw error;
    }
  }

  /**
   * Get user info from Google
   * @param {string} accessToken - The access token
   * @returns {Promise<Object>} User info
   */
  async getUserInfo(accessToken) {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get user info');
      }

      const userInfo = await response.json();
      return userInfo;
    } catch (error) {
      logger.error('Error getting user info:', error);
      throw error;
    }
  }

  /**
   * Revoke token (logout)
   * @param {string} token - Access or refresh token to revoke
   */
  async revokeToken(token) {
    try {
      await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      logger.log('Token revoked successfully');
    } catch (error) {
      logger.error('Error revoking token:', error);
      // Don't throw - revocation failure shouldn't block logout
    }
  }
}

const googleOAuthService = new GoogleOAuthService();
export default googleOAuthService;

